<script setup lang="ts">
import { ref, onMounted, computed, toRaw, watch } from "vue";

import { SpeckleMaterial } from "@/types/Views/MappingWizard/SpeckleMaterial";

import useLifetimeTable from "@/providers/LifetimeTable/LifetimeTableProvider";
import { useCategoryTranslations } from "@/dataProviders/CategoryTranslations";

import BIMFormat from "@/services/Utils/BIMFormat";

import Button from "@/components/Reusables/Buttons/Button.vue";
import NumberInput from "@/components/Reusables/Inputs/NumberInput.vue";

import { useModalStore } from "@/stores/modals/modals.store";
import i18n from "@/services/i18n";

const modals = useModalStore();
const categoryTranslations = useCategoryTranslations();

const props = defineProps<{
  speckleMaterials: Array<SpeckleMaterial>;
  groupMaterial: SpeckleMaterial;
}>();

const emits = defineEmits(["doneSubmitting", "cancel", "close"]);

const lifetimeTable = useLifetimeTable();

const copyMaterials = ref<[SpeckleMaterial]>(JSON.parse(JSON.stringify(toRaw(props.speckleMaterials))));

const materialLifetimes = ref<Array<any>>([]);
const lifeTimeUpdater = ref<number>(0);
const activeSuggestions = ref<{ [key: string]: number }>({});
const assignToAllValue = ref<string>("");

const isSaving = ref<boolean>(false);
const hasValidated = ref<boolean>(false);

const toggleModal = () => {
  emits("cancel");
};

onMounted(async () => {
  initMaterialLifetimes();
});

const openLifetimeTableModal = async (material: any) => {
  const data = await modals.open(
    "LifetimeTable",
    {
      focusConstructionId: material.bimTypeCode,
      focusConstructionIdAlternate: material.alternateBimTypeCode,
    },
    { stackable: true, wrapperClass: "max-w-[90vw] min-h-0" }
  );
  if (data) {
    setNewLifeTime(material.id, data.value, data.constructionIdAlternate);
    setActiveSuggestions(material.id, 0);
  }
};

function setNewLifeTime(id: string, newLT: number, alternateBimTypeCode?: string) {
  materialLifetimes.value = materialLifetimes.value.map((material) => ({
    ...material,
    ...(material.id === id ? { newLifeTime: newLT, alternateBimTypeCode: alternateBimTypeCode || "" } : {}),
  }));
  lifeTimeUpdater.value++;
}

function setActiveSuggestions(id: string, key: number) {
  activeSuggestions.value = {
    ...activeSuggestions.value,
    [id]: key,
  };
}

function handleLifetimeInput(materialId: string) {
  setActiveSuggestions(materialId, 0);
  // Clear validation error when user enters a valid value
  const materialLifetime = materialLifetimes.value.find((m) => m.id === materialId);
  if (
    hasValidated.value &&
    materialLifetime &&
    materialLifetime.newLifeTime !== null &&
    materialLifetime.newLifeTime !== undefined
  ) {
    hasValidated.value = false;
  }
}

// Check if a material has a valid lifetime
const getMaterialError = (materialId: string): string | undefined => {
  if (!hasValidated.value) return undefined;
  const material = materialLifetimes.value.find((m) => m.id === materialId);
  if (!material) return undefined;
  if (material.newLifeTime === null || material.newLifeTime === undefined) {
    return "Please enter a lifetime value";
  }
  return undefined;
};

// Validate all inputs
const validateInputs = computed(() => {
  return materialLifetimes.value.every((material) => material.newLifeTime !== null && material.newLifeTime !== undefined);
});

const submit = async () => {
  hasValidated.value = true;
  // Validate inputs before submitting
  if (!validateInputs.value) {
    isSaving.value = false;
    return;
  }

  // Assing new lifetime to materials
  isSaving.value = true;
  const childrenIds: Array<string> = [];
  copyMaterials.value.map((speckleMaterial) => {
    const materialLifetime = materialLifetimes.value.find((material) => material.id === speckleMaterial.id);

    if (materialLifetime.newLifeTime === null || materialLifetime.newLifeTime === undefined || !speckleMaterial.id) {
      isSaving.value = false;
      return;
    }

    speckleMaterial.newLifeTime = materialLifetime.newLifeTime;
    speckleMaterial.lifeTime = materialLifetime.newLifeTime;
    childrenIds.push(speckleMaterial.id);
  });

  // Toggles loading in co2budget progressbar
  // newMappingStore().isUpdatingCO2Budget = true;
  // Updates co2-budget progress bar

  // Update lifetimes on each material

  // // Reset newLifeTime prop
  // speckleMaterials.value.map((speckleMaterial) => {
  //     speckleMaterial.newLifeTime = null;
  // });

  // isSaving.value = false;

  // // Validate
  // emits("doneSubmitting");
  // // Close modal with result indicating submission
  // modals.close(true);
};

async function initMaterialLifetimes() {
  // Refreh lifetime table
  await lifetimeTable.refreshLifetimeTable();
  // Map copyMaterials to materialLifetimes
  materialLifetimes.value = copyMaterials.value.map((speckleMaterial) => {
    const bimTypeCode: string | null = speckleMaterial.additionalParameters?.BIMTypeCode || null;

    const suggestions: { [key: string]: number } = bimTypeCode
      ? lifetimeTable.getLifetimeTable()?.rows.find((row) => BIMFormat(row.constructionId) === bimTypeCode)?.materialLifeTime ||
        {}
      : {};

    // SuggestionsArray is suggestions entries mapped to an array and should be sorted by key
    const suggestionsArray = Object.entries(suggestions)
      .map(([key, value]) => ({ name: key, value: value }))
      .sort((a, b) => a.name.localeCompare(b.name));

    const { category, identifier, id } = speckleMaterial;

    return {
      id,
      category,
      identifier,
      newLifeTime: null,
      bimTypeCode,
      alternateBimTypeCode: "",
      showLifetimeTable: false,
      suggestions: suggestionsArray,
    };
  });
}

const assignToAll = () => {
  materialLifetimes.value = materialLifetimes.value.map((material) => ({
    ...material,
    newLifeTime: assignToAllValue.value,
  }));

  // There is a better way of doing this, but for now, this will do as it will trigger a re-render and we did it already
  lifeTimeUpdater.value++;
};

const title = computed(() => {
  return `Please set a lifetime related to the
            ${copyMaterials.value.length > 1 ? "types/materials" : "type/material"} you are trying to map`;
});

const categoryLabel = (category?: string | null) =>
  category ? categoryTranslations.translate(category, [i18n.global.locale.value]).value : "";

watch(
  () => activeSuggestions,
  (_newChild) => {},
  { deep: true }
);
</script>

<template>
  <div class="flex flex-col">
    <h4 class="t-h4 text-type-default pb-1.5">
      {{ groupMaterial.identifier }}
    </h4>
    <span class="text-md">
      {{ title }}
    </span>

    <div
      v-if="materialLifetimes.length > 1"
      class="rounded-4 mt-4 flex justify-end gap-3 bg-slate-100 p-3 text-sm font-bold text-slate-500">
      <NumberInput
        v-model:value="assignToAllValue"
        color="primary"
        class="max-w-56"
        placeholder="Enter lifetime"
        align-right
        :max="300"
        positive
        no-decimals
        @keyup.enter="assignToAll" />
      <Button
        label="Assign to all"
        color="button"
        @click="assignToAll" />
    </div>
    <!-- Materials -->
    <div
      v-for="material in materialLifetimes"
      :key="material.id"
      class="mt-4 flex flex-col">
      <!-- Material -->
      <div class="rounded-4 flex flex-col gap-3 bg-slate-100 p-3">
        <p class="text-sm text-slate-500">
          <span class="font-bold whitespace-normal">
            <span v-if="material.category">{{ categoryLabel(material.category) }}:</span>
            {{ material.identifier }}
          </span>
          <span
            v-if="material.bimTypeCode"
            class="ml-2">
            (Classification Code: {{ material.bimTypeCode }})
          </span>
        </p>

        <div class="grid grid-cols-6 gap-4">
          <!-- Suggestions - have BIM Type Code -->
          <div
            v-if="material.bimTypeCode"
            class="rounded-4 col-span-4 flex flex-col bg-slate-200 py-2 text-sm">
            <div class="flex flex-col text-sm">
              <div
                v-for="suggestion in material.suggestions"
                :key="suggestion.name"
                class="flex cursor-pointer items-center justify-between px-4 py-1 transition-all select-none hover:bg-slate-300"
                :class="{
                  'font-bold': activeSuggestions[material.id] === suggestion.name,
                }"
                :onClick="
                  () => {
                    setNewLifeTime(material.id, Number(suggestion.value));
                    setActiveSuggestions(material.id, suggestion.name);
                  }
                ">
                <span>{{ suggestion.name }}</span>
                <span>{{ suggestion.value }} years</span>
              </div>
            </div>
          </div>

          <!-- Suggestions - no BIM Type Code -->
          <div
            v-if="!material.bimTypeCode"
            class="rounded-4 col-span-4 flex w-full flex-col justify-center bg-slate-200 p-8 text-center text-sm">
            <p>
              To use suggestions,
              <br />please add a Classification Code to your material.
            </p>
          </div>

          <!-- Lifetime table and input -->
          <div class="col-span-2 flex flex-col justify-end gap-4">
            <Button
              label="Lifetime table"
              color="button"
              @click="() => openLifetimeTableModal(material)" />

            <NumberInput
              :key="lifeTimeUpdater"
              v-model:value="material.newLifeTime"
              color="primary"
              placeholder="Enter new lifetime"
              align-right
              :error-msg="getMaterialError(material.id)"
              :max="300"
              positive
              no-decimals
              @keyup.enter="submit"
              @keyup="() => handleLifetimeInput(material.id)" />
          </div>
        </div>
      </div>
    </div>

    <div class="mt-4 flex justify-between">
      <Button
        label="Cancel"
        color="button"
        @click="toggleModal" />
      <Button
        :label="isSaving ? 'Saving...' : 'Save'"
        color="primary"
        :is-loading="isSaving"
        @click="submit" />
    </div>
  </div>
</template>
